import React, {
  useState,
} from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useFooterContext } from 'Context/FooterContext';
import Portal from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Portal';
import BaseFormLayout from 'Components/ComponentLibrary/Templates/Unauthenticated/Content/BaseFormLayout';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import TextButton from 'Components/ComponentLibrary/Components/Uncategorized/TextButton';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import { pickFunction } from 'Helpers/Affordances';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useNewLoginContext } from '../../../NewLoginContext';

const styles = (theme) => ({
  secondaryButton: {
    marginTop: theme.spacing(2),
  },
});

const MultiFactorAuth = ({
  classes,
}) => {
  const { resource, onSubmit, unsourcedErrors } = useMetaLayoutContext();
  const { footerRef } = useFooterContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const { handleSubmit } = useNewLoginContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loginViaCode = async event => {
    setIsSubmitting(true);
    if (event) {
      event.preventDefault();
    }
    const coll = await onSubmit();
    if (coll) {
      await handleSubmit(coll);
    }
    setIsSubmitting(false);
  };

  const handleResend = async event => {
    setIsSubmitting(true);
    if (event) {
      event.preventDefault();
    }
    try {
      resource.attr('resendCode', 'true');
      const coll = await resource.submit();
      switch (coll.meta().type()) {
        case 'AuthLoginMultiFactor':
          displaySuccess('Verification code sent');
          break;
          /* no default */
      }
      displaySuccess('Verification code sent');
      await handleSubmit(coll);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchToAuthenticatorApp = async event => {
    setIsSubmitting(true);
    if (event) {
      event.preventDefault();
    }

    const [mfaPreferenceRes] = await resource.relationship(fr.relationships.mfaPreference.name).link('related').get();
    mfaPreferenceRes.attr('preference', 'totp');
    const coll = await mfaPreferenceRes.submit();
    if (coll) {
      await handleSubmit(coll);
    }
    setIsSubmitting(false);
  };

  const renderTextButton = () => {
    return pickFunction(resource, {
      [fr.relationships.mfaPreference.name]: () => {
        return (
          <TextButton
            className={classes.secondaryButton}
            disableElevation
            disabled={isSubmitting}
            color={'primary'}
            variant={'underlined'}
            fullWidth
            onClick={switchToAuthenticatorApp}
            test={'switch-to-authenticator-app'}
          >
            {'Enter code from authenticator app'}
          </TextButton>
        );
      },
      [fr.relationships.default.name]: () => {
        return (
          <TextButton
            className={classes.secondaryButton}
            disabled={isSubmitting}
            onClick={handleResend}
            test={'resend-code-button'}
            fullWidth
          >
            {'Send code again'}
          </TextButton>
        );
      },
    });
  };

  return (
    <BaseFormLayout
      onSubmit={loginViaCode}
      id={'multi-factor-authentication-form-login'}
      title={'Login to your account'}
      subtitle={`Enter the six digit code we texted to ${resource.attr('sentTo')}`}
      errors={unsourcedErrors}
      message={resource.attr('message')}
    >
      <MetaField
        attr={'code'}
        inputProps={{
          test: 'mfa-code-field',
        }}
      />

      <MetaField
        attr={'rememberDevice'}
        test="remember-device-field"
      />

      <Portal container={footerRef.current}>
        <RoundedButton
          onClick={loginViaCode}
          disabled={isSubmitting}
          test={'login-button'}
          type={'submit'}
          form={'multi-factor-authentication-form-login'}
          fullWidth
        >
          {'Login'}
        </RoundedButton>
        {renderTextButton()}
      </Portal>
    </BaseFormLayout>
  );
};

MultiFactorAuth.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MultiFactorAuth);
